import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DotsThreeVertical, Copy, Archive, ArrowCounterClockwise, Trash, MapPin, Buildings } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { formatCompact, formatPct } from "@/lib/format";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export function ProjectCard({ project: p, onChanged, index }) {
  const navigate = useNavigate();
  const t = p.totals;
  const profit = t.profit >= 0;
  const priced = t.item_count ? Math.round((t.priced_count / t.item_count) * 100) : 0;

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const stop = (e) => e.stopPropagation();

  return (
    <div
      data-testid={`project-card-${p.project_id}`}
      onClick={() => navigate(`/projects/${p.project_id}`)}
      style={{ animationDelay: `${index * 50}ms` }}
      className="rise-in group relative bg-white border border-slate-200 rounded-sm shadow-sm p-5 cursor-pointer transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
    >
      <span className={`absolute left-0 top-0 h-full w-1 ${profit ? "bg-emerald-600" : "bg-rose-600"}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-bold text-slate-900 leading-snug line-clamp-2" data-testid="project-card-name">{p.name}</h3>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            {p.client && <span className="flex items-center gap-1"><Buildings size={13} />{p.client}</span>}
            {p.location && <span className="flex items-center gap-1"><MapPin size={13} />{p.location}</span>}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger onClick={stop} data-testid={`project-menu-${p.project_id}`} className="p-1.5 rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
            <DotsThreeVertical size={20} weight="bold" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={stop}>
            <DropdownMenuItem data-testid="project-duplicate" onClick={() => act(() => api.post(`/projects/${p.project_id}/duplicate`), "Project duplicated")}>
              <Copy className="mr-2" size={15} /> Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="project-archive-toggle"
              onClick={() => act(() => api.put(`/projects/${p.project_id}`, { status: p.status === "archived" ? "active" : "archived" }), p.status === "archived" ? "Project restored" : "Project archived")}
            >
              {p.status === "archived" ? <><ArrowCounterClockwise className="mr-2" size={15} /> Restore</> : <><Archive className="mr-2" size={15} /> Archive</>}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="project-delete"
              className="text-rose-600 focus:text-rose-700"
              onClick={() => window.confirm(`Delete "${p.name}" permanently?`) && act(() => api.delete(`/projects/${p.project_id}`), "Project deleted")}
            >
              <Trash className="mr-2" size={15} /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Govt BOQ</div>
          <div className="font-heading text-lg font-bold text-slate-900 tabular">{formatCompact(t.govt_amount)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Expected P/L</div>
          <div className={`font-heading text-lg font-bold tabular ${profit ? "text-emerald-600" : "text-rose-600"}`}>{formatCompact(t.profit)}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">Margin</div>
          <div className={`font-heading text-lg font-bold tabular ${profit ? "text-emerald-600" : "text-rose-600"}`}>{formatPct(t.margin)}</div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex justify-between text-[11px] text-slate-500 mb-1">
          <span>{t.priced_count}/{t.item_count} items priced</span>
          <span>{p.status === "archived" ? "Archived" : priced < 100 && t.item_count ? `${priced}% · P/L provisional` : `${priced}%`}</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
          <div className="h-full bg-slate-900 transition-[width] duration-500" style={{ width: `${priced}%` }} />
        </div>
      </div>
    </div>
  );
}
